import * as React from 'react';
import { useContext } from 'react';
import { ApiError, apiPost, logApiError } from "../api";
import Dump from "../components/dump";
import Spinner, { SpinnerColorsEnum } from "../components/spinner";
import { isFunction } from "../helpers/utils";
import { stepBack } from "../loaders";
import ColumnMain from "../src/columnMain";
import ColumnSide from "../src/columnSide";
import Layout from "../src/layout";
import { DayPicker } from "react-day-picker";
import Summary from "../src/summary";
import { ContextSession } from "./app";
import { useForm, Controller, type SubmitHandler, type SubmitErrorHandler } from 'react-hook-form';
import HookFormDevToolLazy from "../../js/components/hookFormDevToolLazy";
import ProductDescription from "../components/productDescription";
import { resolvePlanName } from "./planNameOverrides";
import { formatDate, formatPriceWithCurrency, getOfferAdminFee, getOfferIntervalName, getOfferPrice, isSubscription, parseStartDate } from "./utils";
import { ErrorMessage } from "@hookform/error-message";
import LockedNotice from "../components/lockedNotice";
import { DiscountBanner, useAvailableDiscount } from "../components/discount";

type Props = Readonly<{
	setState: Function,
	offer?: {},
	memberLoginUrl?: string | null,
}>

type MembershipFormValues = {
	startDate: Date,
}

function Membership({setState, offer, memberLoginUrl, ...otherProps}: Props) {
	let today = new Date();

	// Automatic renewal, a pro rata start month and the admin fee all describe a
	// recurring membership. A one-off product has none of them.
	const subscription = isSubscription(offer);
	const adminFee = getOfferAdminFee(offer);

	const state = useContext(ContextSession);

	// Surface the club's discount for this plan as early as here, not just at payment.
	const discount = useAvailableDiscount(state.hash, subscription);

	// Past the point of no return: the start date is baked into the mandate, so
	// the picker is shown read-only and the customer is offered a restart.
	const committed = Boolean(state?.mandateId || state?.payableId);

	const {
		handleSubmit,
		formState: { errors, isSubmitting },
		control,
		watch,
		setError,
	} = useForm<MembershipFormValues>({
		defaultValues: {
			// Resume on the exact calendar day stored (parsed as a local day, not a
			// timezone-shifted instant); otherwise default to today.
			startDate: parseStartDate(state.startDate) ?? today,
		}
	});

	const formStartDate = watch("startDate");

	const onSubmit: SubmitHandler<MembershipFormValues> = async (data) => {
		// console.log(data);

		try {
			const response = await apiPost('/resa/startDate/' + state?.hash, {...data, startDate: formatDate(data.startDate)});
			if (isFunction(setState)) {
				setState(response.data);
			}
		} catch (error) {
			if (error instanceof ApiError) {
				setError("root.serverError", {
					type: String(error.status),
					message: 'Error has occurred. Check your entered data and try again.'
				});
				logApiError(error);
			}
		}
	};

	const onError: SubmitErrorHandler<MembershipFormValues> = (errors) => {
		console.log(errors);
	}

	return (<>
		<Layout name={"YOUR MEMBERSHIP"}>
			<ColumnMain innerClassName="" banner={discount ? <DiscountBanner discount={discount}/> : null}>
				<div className="relative">
				{/* Once committed the whole form is inert and the overlay below fades it. */}
				<form onSubmit={handleSubmit(onSubmit, onError)} inert={committed || undefined}>
					<HookFormDevToolLazy control={control}/>

					<div className="p-6 sm:p-8 bg-primary-200">
						<h3>{subscription ? 'SELECTED MEMBERSHIP PLAN' : 'SELECTED PRODUCT'}</h3>
						{/*
							offer.name is null on the API — the plan's name lives on the
							showcase row, so it is snapshotted onto the session when the plan
							is chosen and served back on the offer payload. Resolved through
							the same overrides the plan cards use.
						*/}
						<h5 className="mb-8 uppercase">{resolvePlanName(offer?.showcaseIri, offer?.name)}</h5>

						{subscription && <ul className="mb-8">
							<li>Membership with automatic renewal</li>
							{/*
								`adminFee &&` rendered a bare "0" on screen: getOfferAdminFee
								returns the number 0 for a fee-free offer, and React prints 0
								rather than treating it as blank the way it does false/null.
							*/}
							{adminFee > 0 && <li>Admin fees: {formatPriceWithCurrency(adminFee, offer?.priceCurrency)}</li>}
							<li>Start month price: pro rata</li>
						</ul>}

						<div className="flex flex-wrap gap-8 justify-between text-lg">
							<div className="font-bold">
								<span className="font-black text-3xl">
									{formatPriceWithCurrency(getOfferPrice(offer), offer?.priceCurrency)}
								</span>
								{/*
									A one-off product has no interval — getOfferIntervalName
									returns '' for it, which rendered a dangling "£20.00 / ".
								*/}
								{subscription && <span> / {getOfferIntervalName(offer)}</span>}
							</div>
							<div>

								<button className="button !mb-0 white" onClick={(event) => {
									console.log('step back');
									event.preventDefault();
									stepBack(state.hash, setState);
								}}>SELECT A DIFFERENT OPTION</button>
							</div>
						</div>
					</div>

					<div className="p-6 sm:p-8">
						{offer?.description && <>
							{/* "PLAN" only fits a subscription; a one-off product is just details. */}
							<h5 className="mb-8">{subscription ? 'PLAN DETAILS' : 'DETAILS'}</h5>

							<ProductDescription description={offer.description}/>

							<hr/>
						</>}

						<h5 className="mb-8">START DATE</h5>

						<div className="flex flex-wrap -mx-4">
							<div className="w-full xl:w-1/2 px-4">
								<p>Not ready to start today? Choose a date that suits and we’ll work out the payment plan</p>
							</div>
							<div className="w-full xl:w-1/2 px-4">
								{/*<input*/}
								{/*	type="text"*/}
								{/*	placeholder={startDateValue?.toISOString().split('T')[0]}*/}
								{/*	className="w-full"*/}
								{/*	disabled*/}
								{/*/>*/}

								<Controller
									name="startDate"
									control={control}
									rules={{ required: true }}
									render={({ field: { onChange, onBlur, value, ref } }) => {
										// console.log(value);
										return (
											<DayPicker
												animate
												required={true}
												mode="single"
												disabled={{before: today}}
												selected={new Date(value)}
												onSelect={(selected) => {
													// console.log(selected);
													onChange(selected);
												}}
											/>
										);
									}}
								/>
							</div>
						</div>

						<hr className="my-8"/>

						<div className="text-red-400 mb-8">
							<ErrorMessage errors={errors} name={'root.serverError'}/>
						</div>

						<div className="flex flex-wrap gap-x-4 sm:gap-x-8 gap-y-4 justify-center">
							<button className="grey !mb-0" onClick={(event) => {
								event.preventDefault();
								stepBack(state.hash, setState);
							}}>BACK</button>
							<button type="submit" className="mb-0 flex items-center justify-center" disabled={isSubmitting}>
								NEXT
								{isSubmitting && <Spinner className="!w-4 !h-4" color={SpinnerColorsEnum.Black}/>}
							</button>
						</div>
					</div>
				</form>
				{committed && <LockedNotice hash={state.hash} setState={setState} memberLoginUrl={memberLoginUrl}/>}
				</div>
			</ColumnMain>
			<ColumnSide className="hidden lg:block">
				<Summary offer={offer} startDate={new Date(formStartDate)} discount={discount}/>
			</ColumnSide>
		</Layout>

		{/*<Dump value={state}/>*/}
		{/*<Dump value={offer}/>*/}
		{/*<Dump value={state?.hash}/>*/}
	</>
)
}

export default Membership;
